import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { listAdminContent, getAdminContent, updateAdminContent, uploadAdminPhoto } from "@/lib/api";
import { Loader2, Save, Upload, X } from "lucide-react";
import { toast } from "sonner";

const defaultHero = {
    eyebrow: "Dentiprime · Édition Professionnelle",
    title: "L'équipement dentaire conçu pour l'excellence.",
    title_highlight: "l'excellence",
    subtitle: "Turbines, implantologie, endodontie et instruments de précision sélectionnés pour les professionnels les plus exigeants.",
    cta_catalog: "Découvrir le catalogue",
    cta_quote: "Demander un devis",
    visual_image: "",
    visual_alt: "Turbine dentaire haute performance",
    floating_chips: [
        { label: "Tête", value: "45° · Push button", position: "top-right" },
        { label: "Rotor", value: "≤ 420 000 tr/min", position: "bottom-left" },
        { label: "Spray", value: "4 jets · Eau / Air", position: "bottom-right" },
    ],
};

const HeroForm = ({ data, onChange }) => {
    const [uploading, setUploading] = useState(false);
    const values = { ...defaultHero, ...(data || {}) };

    const handleTextChange = (field, value) => {
        onChange({ ...values, [field]: value });
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const res = await uploadAdminPhoto(file);
            if (res.url) {
                onChange({ ...values, visual_image: res.url });
                toast.success("Image téléchargée.");
            } else {
                toast.error("Échec du téléchargement.");
            }
        } catch (err) {
            toast.error(err.message || "Échec du téléchargement.");
        } finally {
            setUploading(false);
        }
    };

    const clearImage = () => {
        onChange({ ...values, visual_image: "" });
    };

    return (
        <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-1.5">
                    <Label>Eyebrow</Label>
                    <Input
                        value={values.eyebrow}
                        onChange={(e) => handleTextChange("eyebrow", e.target.value)}
                        placeholder="Dentiprime · Édition Professionnelle"
                    />
                </div>
                <div className="space-y-1.5">
                    <Label>Titre mis en valeur</Label>
                    <Input
                        value={values.title_highlight}
                        onChange={(e) => handleTextChange("title_highlight", e.target.value)}
                        placeholder="l'excellence"
                    />
                </div>
            </div>

            <div className="space-y-1.5">
                <Label>Titre</Label>
                <Input
                    value={values.title}
                    onChange={(e) => handleTextChange("title", e.target.value)}
                    placeholder="L'équipement dentaire conçu pour l'excellence."
                />
            </div>

            <div className="space-y-1.5">
                <Label>Sous-titre</Label>
                <Textarea
                    value={values.subtitle}
                    onChange={(e) => handleTextChange("subtitle", e.target.value)}
                    placeholder="Turbines, implantologie..."
                    rows={3}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-1.5">
                    <Label>CTA Catalogue</Label>
                    <Input
                        value={values.cta_catalog}
                        onChange={(e) => handleTextChange("cta_catalog", e.target.value)}
                        placeholder="Découvrir le catalogue"
                    />
                </div>
                <div className="space-y-1.5">
                    <Label>CTA Devis</Label>
                    <Input
                        value={values.cta_quote}
                        onChange={(e) => handleTextChange("cta_quote", e.target.value)}
                        placeholder="Demander un devis"
                    />
                </div>
            </div>

            <div className="space-y-3">
                <Label>Badges flottants sur l&apos;image</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {(values.floating_chips || []).map((chip, i) => (
                        <div key={chip.position} className="space-y-2 p-3 rounded-lg border border-neutral-100 bg-neutral-50">
                            <div className="text-[11px] uppercase tracking-wider text-neutral-400 font-medium">
                                {chip.position === "top-right" ? "Haut droite" : chip.position === "bottom-left" ? "Bas gauche" : "Bas droite"}
                            </div>
                            <Input
                                value={chip.label}
                                onChange={(e) => {
                                    const next = [...(values.floating_chips || [])];
                                    next[i] = { ...chip, label: e.target.value };
                                    onChange({ ...values, floating_chips: next });
                                }}
                                placeholder="Label"
                            />
                            <Input
                                value={chip.value}
                                onChange={(e) => {
                                    const next = [...(values.floating_chips || [])];
                                    next[i] = { ...chip, value: e.target.value };
                                    onChange({ ...values, floating_chips: next });
                                }}
                                placeholder="Valeur"
                            />
                        </div>
                    ))}
                </div>
            </div>

            <div className="space-y-1.5">
                <Label>Image principale (remplace la turbine SVG)</Label>
                <div className="flex items-center gap-4">
                    <label className="relative cursor-pointer">
                        <Input
                            type="file"
                            accept="image/png,image/jpeg,image/jpg,image/webp"
                            onChange={handleFileChange}
                            disabled={uploading}
                            className="absolute inset-0 opacity-0 cursor-pointer"
                        />
                        <Button type="button" variant="outline" disabled={uploading} className="rounded-full">
                            {uploading ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                                <Upload className="w-4 h-4 mr-2" />
                            )}
                            {uploading ? "Envoi..." : "Choisir une image"}
                        </Button>
                    </label>
                    {values.visual_image && (
                        <Button type="button" variant="ghost" size="sm" onClick={clearImage} className="text-red-600">
                            <X className="w-4 h-4 mr-1" /> Supprimer
                        </Button>
                    )}
                </div>
                {values.visual_image && (
                    <div className="mt-3 space-y-1.5">
                        <Label>Texte alternatif de l&apos;image</Label>
                        <Input
                            value={values.visual_alt}
                            onChange={(e) => handleTextChange("visual_alt", e.target.value)}
                            placeholder="Turbine dentaire haute performance"
                        />
                        <div className="relative w-full max-w-xs aspect-square rounded-xl border border-neutral-200 overflow-hidden bg-white">
                            <img
                                src={values.visual_image}
                                alt={values.visual_alt}
                                className="w-full h-full object-contain"
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};


const defaultHeroTop = {
    enabled: true,
    text: "Livraison gratuite à partir de 600 DH",
};

const HeroTopForm = ({ data, onChange }) => {
    const values = { ...defaultHeroTop, ...(data || {}) };
    return (
        <div className="space-y-4">
            <div className="flex items-center gap-3">
                <input
                    id="hero_top_enabled"
                    type="checkbox"
                    checked={values.enabled}
                    onChange={(e) => onChange({ ...values, enabled: e.target.checked })}
                    className="w-4 h-4 rounded border-neutral-300 text-[#0a84ff] focus:ring-[#0a84ff]"
                />
                <Label htmlFor="hero_top_enabled" className="cursor-pointer">
                    Afficher la barre en haut de page
                </Label>
            </div>
            <div className="space-y-1.5">
                <Label>Texte défilant</Label>
                <Input
                    value={values.text}
                    onChange={(e) => onChange({ ...values, text: e.target.value })}
                    placeholder="Livraison gratuite à partir de 600 DH"
                />
            </div>
        </div>
    );
};

const Content = () => {
    const queryClient = useQueryClient();
    const [selectedKey, setSelectedKey] = useState("");
    const [json, setJson] = useState("{}");
    const [parseError, setParseError] = useState(null);
    const [heroData, setHeroData] = useState(defaultHero);
    const [heroTopData, setHeroTopData] = useState(defaultHeroTop);

    const { data: sections = [], isLoading } = useQuery({
        queryKey: ["admin", "content"],
        queryFn: listAdminContent,
    });

    const { data: selectedSection, isLoading: loadingSection } = useQuery({
        queryKey: ["admin", "content", selectedKey],
        queryFn: () => getAdminContent(selectedKey),
        enabled: !!selectedKey,
    });

    useEffect(() => {
        if (selectedSection) {
            setJson(JSON.stringify(selectedSection.data, null, 2));
            setParseError(null);
            if (selectedKey === "hero") {
                setHeroData({ ...defaultHero, ...(selectedSection.data || {}) });
            }
            if (selectedKey === "hero_top") {
                setHeroTopData({ ...defaultHeroTop, ...(selectedSection.data || {}) });
            }
        }
    }, [selectedSection, selectedKey]);

    const mutation = useMutation({
        mutationFn: () => {
            const data =
                selectedKey === "hero"
                    ? heroData
                    : selectedKey === "hero_top"
                    ? heroTopData
                    : JSON.parse(json);
            return updateAdminContent(selectedKey, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin", "content"] });
            queryClient.invalidateQueries({ queryKey: ["content", selectedKey] });
            toast.success("Contenu enregistré.");
            setParseError(null);
        },
        onError: (err) => {
            if (err instanceof SyntaxError) {
                setParseError("JSON invalide : " + err.message);
            } else {
                toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement.");
            }
        },
    });

    const handleSave = () => {
        if (selectedKey === "hero" || selectedKey === "hero_top") {
            mutation.mutate();
            return;
        }
        try {
            JSON.parse(json);
            setParseError(null);
            mutation.mutate();
        } catch (err) {
            setParseError("JSON invalide : " + err.message);
        }
    };

    return (
        <AdminLayout>
            <div className="space-y-6">
                <div>
                    <h1 className="font-display text-3xl font-semibold tracking-tight">
                        Contenu
                    </h1>
                    <p className="text-neutral-500 mt-1">
                        Éditez les sections dynamiques du site.
                    </p>
                </div>

                <div className="bg-white rounded-xl border border-neutral-200 p-5 space-y-4">
                    <div className="space-y-1.5">
                        <Label>Section</Label>
                        <Select value={selectedKey} onValueChange={setSelectedKey}>
                            <SelectTrigger>
                                <SelectValue placeholder="Choisir une section" />
                            </SelectTrigger>
                            <SelectContent>
                                {sections.map((s) => (
                                    <SelectItem key={s.key} value={s.key}>
                                        {s.key}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {selectedKey && loadingSection && (
                        <div className="py-12 flex justify-center">
                            <Loader2 className="w-6 h-6 animate-spin text-[#0a84ff]" />
                        </div>
                    )}

                    {selectedKey && !loadingSection && (
                        <>
                            {selectedKey === "hero" ? (
                                <HeroForm data={heroData} onChange={setHeroData} />
                            ) : selectedKey === "hero_top" ? (
                                <HeroTopForm data={heroTopData} onChange={setHeroTopData} />
                            ) : (
                                <div className="space-y-1.5">
                                    <Label>Données JSON</Label>
                                    <Textarea
                                        value={json}
                                        onChange={(e) => setJson(e.target.value)}
                                        rows={20}
                                        className="font-mono text-sm"
                                    />
                                </div>
                            )}

                            {parseError && (
                                <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                                    {parseError}
                                </div>
                            )}
                            <Button
                                onClick={handleSave}
                                disabled={mutation.isPending}
                                className="bg-[#1C1C1E] hover:bg-black rounded-full"
                            >
                                {mutation.isPending && (
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                )}
                                <Save className="w-4 h-4 mr-2" />
                                Enregistrer
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
};

export default Content;
